// src/components/Modals/ModalProgramacion/ModalMatchUniformHistory.jsx
/**
 * PURPOSE: "Historial de uniformes" de un partido de Programación (FTR-0044), desde el cuarto ícono del modal.
 * RESPONSIBILITIES: Mostrar, solo para uniformes (camiseta principal y alterno de cada lado): la asignación
 *   actual; cada cambio guardado, del más reciente al más antiguo, con origen (algoritmo automático, cambio
 *   manual, asignación masiva, restauración de versión, edición de texto), fecha y hora de Lima, usuario,
 *   antes → después y la explicación guardada al decidir (lo anterior sin explicación lo dice, no la inventa);
 *   y, con el versionamiento del evento activo (FTR-0046), el valor del partido en cada versión del torneo (la que
 *   está en uso es lo que muestra el partido). Solo lectura.
 * COLLABORATORS: uniformOptionsService.js (getMatchUniformHistory), uniformVersionsDisplay.js,
 *   UniformOptionVisual.jsx, ModalProgramacion.jsx.
 * ROUTE: winscore/src/components/Modals/ModalProgramacion/ModalMatchUniformHistory.jsx
 */
import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";

import UniformOptionVisual from "../../../admin/AdminCompany/UniformOptionVisual.jsx";
import { getUniformErrorMessage } from "../../../admin/AdminCompany/uniformOptions.utils.js";
import { getMatchUniformHistory } from "../../../services/uniformOptionsService.js";
import {
  HISTORY_ORIGIN_TONES,
  formatLimaDateTimeShort,
  historyReasonText,
  newestFirst,
  slotValueText,
  versionTitle,
} from "../../../utils/uniformVersionsDisplay.js";

const BADGE_CLASSES = {
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  sky: "bg-sky-50 text-sky-800 ring-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  violet: "bg-violet-50 text-violet-800 ring-violet-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};
const SEVERITY_CLASSES = { STRONG: "text-rose-700", WARNING: "text-amber-800", INFO: "text-slate-600" };
const MODE_TEXT = { COMPLETE: "Completar faltantes", RECALCULATE: "Recalcular" };
const sideWord = (side) => (Number(side) === 1 ? "local" : "visitante");
const institutionName = (institution) => institution?.abrev || institution?.name || "Por definir";

const ValueCell = ({ value }) => {
  const text = slotValueText(value);
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {value?.option ? <UniformOptionVisual option={value.option} size="xs" swatchSize="sm" showPlaceholder={false} alt={text} /> : null}
      <span className={value?.option || value?.text ? "text-slate-800" : "italic text-slate-400"}>{text}</span>
    </span>
  );
};

const HistoryEvent = ({ event }) => {
  const reason = historyReasonText(event);
  return (
    <li className="rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className={`rounded-full px-2 py-0.5 font-bold ring-1 ring-inset ${BADGE_CLASSES[HISTORY_ORIGIN_TONES[event.origin]] || BADGE_CLASSES.slate}`}>
          {event.originLabel || event.origin}
        </span>
        <time>{formatLimaDateTimeShort(event.at)}</time>
        <span>· {event.user?.name || "Usuario no identificado"}</span>
        {event.mode ? <span>· {MODE_TEXT[event.mode] || event.mode}</span> : null}
      </div>
      <p className="mt-1 text-sm font-semibold text-slate-800">
        {event.kindLabel || "Uniforme"} {event.side ? sideWord(event.side) : ""} · {institutionName(event.institution)}
      </p>
      <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm">
        <ValueCell value={event.before} />
        <span className="text-slate-400">→</span>
        <ValueCell value={event.after} />
      </p>
      {reason ? <p className={`mt-1 text-xs ${event.reason ? "text-slate-700" : "italic text-slate-500"}`}>{reason}</p> : null}
      {event.notes?.length ? (
        <ul className="mt-1 space-y-0.5 text-xs">
          {event.notes.map((note, index) => (
            <li key={`${note.code}-${index}`} className={SEVERITY_CLASSES[note.severity] || SEVERITY_CLASSES.INFO}>
              {note.severity === "STRONG" ? "⚠⚠" : note.severity === "WARNING" ? "⚠" : "ℹ"} {note.message}
            </li>
          ))}
        </ul>
      ) : null}
      {event.version?.before ? (
        <p className="mt-1 text-xs text-slate-500">Antes de aplicar se guardó el estado anterior como V{event.version.number}.</p>
      ) : null}
    </li>
  );
};

const ModalMatchUniformHistory = ({ open, setOpen, match }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const idmatch = match?.idmatch;

  useEffect(() => {
    if (!open || !idmatch) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setHistory(null);
    getMatchUniformHistory(idmatch)
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch((err) => {
        if (!cancelled) setError(getUniformErrorMessage(err, "No se pudo cargar el historial de uniformes."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idmatch]);

  const kinds = history?.kinds || [];
  const slots = history ? history.sides.flatMap((side) => kinds.map((kind) => ({ key: `${side.side}:${kind.kind}`, side, kind }))) : [];
  const events = newestFirst(history?.events || []);

  return (
    <Modal aria-labelledby="match-uniform-history-title" open={open} onClose={() => setOpen(false)} className="flex items-center justify-center">
      <Sheet variant="outlined" className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-md bg-white p-6 shadow-lg">
        <ModalClose variant="plain" className="m-1" onClick={() => setOpen(false)} />
        <Typography component="h2" id="match-uniform-history-title" level="h4" className="mb-1 text-2xl font-semibold">
          Historial de uniformes
        </Typography>
        <p className="mb-4 text-sm text-gray-500">
          {match?.Participant1?.Institution?.abrev || "Pendiente"} vs {match?.Participant2?.Institution?.abrev || "Pendiente"} · solo
          camisetas y alternos
        </p>

        {loading ? (
          <div className="py-8 text-center text-sm text-gray-500">Cargando...</div>
        ) : error ? (
          <div role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">
            {error}
          </div>
        ) : history ? (
          <div className="space-y-5">
            <section aria-labelledby="uniform-history-current">
              <h3 id="uniform-history-current" className="mb-2 text-sm font-black uppercase tracking-wide text-slate-600">
                Asignación actual
              </h3>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full border-collapse text-sm" aria-label="Asignación actual">
                  <thead>
                    <tr className="bg-slate-100 text-xs font-black uppercase tracking-wide text-slate-600">
                      <th className="px-3 py-2 text-left">Uniforme</th>
                      {history.sides.map((side) => (
                        <th key={side.side} className="px-3 py-2 text-left">
                          {Number(side.side) === 1 ? "Local" : "Visitante"} · <span className="normal-case">{institutionName(side.institution)}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kinds.map((kind) => (
                      <tr key={kind.kind} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold text-slate-700">{kind.label}</td>
                        {history.sides.map((side) => (
                          <td key={side.side} className="px-3 py-2">
                            <ValueCell value={side.current?.[kind.kind]} />
                            {side.current?.[kind.kind]?.stale ? <span className="ml-1 text-xs text-amber-700">(obsoleto)</span> : null}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section aria-labelledby="uniform-history-events">
              <h3 id="uniform-history-events" className="mb-2 text-sm font-black uppercase tracking-wide text-slate-600">
                Cambios ({events.length})
              </h3>
              {events.length === 0 ? (
                <p className="text-sm text-slate-500">Todavía no hay cambios de uniformes registrados para este partido.</p>
              ) : (
                <ol className="space-y-2" aria-label="Cambios de uniformes">
                  {events.map((event) => (
                    <HistoryEvent key={event.id} event={event} />
                  ))}
                </ol>
              )}
            </section>

            {history.versioningEnabled ? (
            <section aria-labelledby="uniform-history-versions">
              <h3 id="uniform-history-versions" className="mb-2 text-sm font-black uppercase tracking-wide text-slate-600">
                En cada versión
              </h3>
              {history.versions.length === 0 ? (
                <p className="text-sm text-slate-500">Este partido no existía en ninguna versión del torneo.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full min-w-[640px] border-collapse text-sm" aria-label="Uniformes en cada versión">
                    <thead>
                      <tr className="bg-slate-100 text-xs font-black uppercase tracking-wide text-slate-600">
                        <th className="px-3 py-2 text-left">Versión</th>
                        {slots.map(({ key, side, kind }) => (
                          <th key={key} className="px-3 py-2 text-left">
                            {kind.label} {sideWord(side.side)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {history.versions.map((version) => (
                        <tr key={version.id} className="border-t border-slate-100 align-top">
                          <td className="px-3 py-2">
                            <span className="font-semibold text-slate-800">{versionTitle(version)}</span>
                            {version.active ? (
                              <span className="ml-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-bold text-violet-800">En uso</span>
                            ) : null}
                            <span className="block text-xs text-slate-500">
                              {version.active
                                ? "Lo que muestra el partido ahora"
                                : `Guardada ${formatLimaDateTimeShort(version.updatedAt || version.createdAt)}`}
                            </span>
                          </td>
                          {slots.map(({ key }) => (
                            <td key={key} className="px-3 py-2">
                              <ValueCell value={version.slots?.[key]} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            ) : null}
          </div>
        ) : null}
      </Sheet>
    </Modal>
  );
};

export default ModalMatchUniformHistory;
